import {
  deferTracking,
  sendGAEvent,
  sendGALinkClickEvent,
} from '@noths/polaris-client-google-analytics';
import { getProductIdFromProductCode } from '@noths/polaris-client-utils';

import type { TrackingProductData } from 'src/tracking/common/products';

/**
 * DISC-4503. The taxonomy shelf reports as a Listings Recommendations surface
 * rather than as a CMS carousel, which is why this block does not use the
 * shared helpers in src/tracking/common the way every other section does.
 *
 * browse-productdetails renders the same shelf and has a near-identical copy of
 * this file. Web apps here cannot share tracking code, so the two are kept in
 * step by hand: change one, change the other. The only intended differences are
 * the `pageType` argument (PDP hardcodes 'product') and content_position, which
 * PDP has no equivalent of and omits.
 */
const EVENT_CATEGORY = 'Listings Recommendations';
const CONTENT_TYPE = 'taxonomy preview carousel';

/** The taxonomy leaf, which is the referenced PLP's heading, lowercased. */
const taxonomyLeaf = (nodeName: string) => nodeName.toLowerCase();

const listValue = (pageType: string, nodeName: string) =>
  `${pageType} - ${CONTENT_TYPE} - ${taxonomyLeaf(nodeName)}`.toLowerCase();

const getTrackedProduct = (product: TrackingProductData, index: number, list?: string) => {
  const {
    code,
    freeDomesticDelivery,
    isNew,
    onSale,
    partner,
    price,
    purchasable,
    salePercentage,
    title,
  } = product;
  const formattedPrice = String(price.amount / 100);

  const dimension22List = [
    freeDomesticDelivery && 'free uk delivery',
    isNew && 'new',
    onSale && !!salePercentage && `-${salePercentage}% off`,
    !purchasable && 'out of stock',
  ]
    .filter(Boolean)
    .join(', ');

  const position = index + 1;

  return {
    name: title,
    id: String(getProductIdFromProductCode(code)),
    dimension22: dimension22List,
    dimension25: position,
    dimension30: String(code),
    dimension31: formattedPrice,
    price: formattedPrice,
    dimension32: formattedPrice,
    dimension33: onSale ? `${salePercentage}.0%` : 'none',
    brand: partner.name,
    dimension34: partner.shortcode,
    position,
    // Impressions carry `list` per product; a click carries it once on the
    // actionField instead, so it is omitted there.
    ...(list && { list }),
    dimension16: 'not set',
  };
};

export interface TrackPlpPreviewImpressionArgs {
  /** The section's 1-indexed position among the page's trackable sections. */
  contentPosition: number;
  nodeName: string;
  /** The page's own pageType, the `<location>` half of the list value. */
  pageType: string;
  products: TrackingProductData[];
}

export const trackPlpPreviewImpression = ({
  contentPosition,
  nodeName,
  pageType,
  products,
}: TrackPlpPreviewImpressionArgs) => {
  if (products.length === 0) {
    return;
  }

  const leaf = taxonomyLeaf(nodeName);
  const list = listValue(pageType, nodeName);

  // Deferred off the critical path. Both pushes share one callback so the
  // ecommerce reset always lands before the payload.
  deferTracking(() => {
    sendGAEvent({ ecommerce: null });

    sendGAEvent({
      event: 'product_impressions',
      event_category: EVENT_CATEGORY,
      event_action: 'Product Impressions',
      event_label: leaf,
      event_name: 'view_item_list',
      content_position: contentPosition,
      content_type: CONTENT_TYPE,
      item_id: leaf,
      total_options: products.length,
      ecommerce: {
        impressions: products.map((product, index) => getTrackedProduct(product, index, list)),
      },
    });
  });
};

export interface TrackPlpPreviewProductClickArgs {
  contentPosition: number;
  nodeName: string;
  numberOfItems: number;
  pageType: string;
  product: TrackingProductData;
  productIndex: number;
}

export const trackPlpPreviewProductClick = (
  e: React.MouseEvent<HTMLAnchorElement>,
  {
    contentPosition,
    nodeName,
    numberOfItems,
    pageType,
    product,
    productIndex,
  }: TrackPlpPreviewProductClickArgs,
) => {
  const leaf = taxonomyLeaf(nodeName);

  sendGAEvent({ ecommerce: null });

  sendGALinkClickEvent(e, {
    event: 'product_clicks',
    event_category: EVENT_CATEGORY,
    event_action: 'Product Click',
    event_label: leaf,
    event_name: 'select_item',
    content_position: contentPosition,
    content_type: CONTENT_TYPE,
    // The leaf, not the product code: the shelf is the thing being reported on.
    item_id: leaf,
    selected_position: productIndex + 1,
    total_options: numberOfItems,
    ecommerce: {
      click: {
        actionField: { list: listValue(pageType, nodeName) },
        products: [getTrackedProduct(product, productIndex)],
      },
    },
  });
};

export interface TrackPlpPreviewSeeAllClickArgs {
  href: string;
  nodeName: string;
}

/**
 * custom_event_v2, so no event_category, event_action or event_label: no
 * custom_event_v2 payload in this codebase carries them.
 */
export const trackPlpPreviewSeeAllClick = (
  e: React.MouseEvent<HTMLAnchorElement>,
  { href, nodeName }: TrackPlpPreviewSeeAllClickArgs,
) => {
  sendGALinkClickEvent(e, {
    event: 'custom_event_v2',
    event_name: 'select_content',
    event_details: 'see all click',
    item_id: taxonomyLeaf(nodeName),
    content_type: CONTENT_TYPE,
    destination_url: `${window.location.origin}${href}`,
  });
};

export interface TrackPlpPreviewNavigationArgs {
  direction: 'next' | 'prev';
  nodeName: string;
  swiped: boolean;
}

/**
 * `direction` stays `'next' | 'prev'`, which is what the design system's
 * ProductCarousel emits. The spelt-out `'previous'` only ever appears in the
 * outgoing event_details string.
 */
export const trackPlpPreviewNavigation = ({
  direction,
  nodeName,
  swiped,
}: TrackPlpPreviewNavigationArgs) => {
  const navigationType = swiped ? 'swipe' : 'arrow click';
  const navigationDirection = direction === 'next' ? 'next' : 'previous';

  // Defer off the interaction's critical path: fire-and-forget, does not gate navigation.
  deferTracking(() =>
    sendGAEvent({
      event: 'custom_event_v2',
      event_name: 'content_interaction',
      event_details: `${navigationType} ${navigationDirection}`,
      item_id: taxonomyLeaf(nodeName),
      content_type: CONTENT_TYPE,
    }),
  );
};
